/**
 * The device's own copy: messages already received, the outbox of messages
 * not yet confirmed by the server, and the sync cursor. One database per
 * person, so a second account on the same phone never mixes with the first.
 */
export interface LocalMessage {
  id: string
  seq: number
  threadId: string
  /** Null for a message from an order's customer. */
  authorPersonId: string | null
  authorCustomer?: boolean
  authorName?: string
  authorOrganisationId: string | null
  authorOrganisationName?: string | null
  body: string
  createdAt: string
  /** "proposal": a sales channel offer, shown as a card (its state from the server); else text */
  kind?: string
  /** Files; null or missing when none */
  attachments?: Attachment[] | null
}

/** A file in a message. Shown through a short-lived link (attachmentUrl). */
export interface Attachment {
  id: string
  name: string
  contentType: string
  size: number
}

export interface OutboxEntry {
  id: string
  threadId: string
  body: string
  asOrganisationId?: string
  /** Uploaded already; the message takes them when it's sent */
  attachments?: Attachment[]
  createdAt: number
  /**
   * pending: not confirmed yet, keeps retrying.
   * sent: confirmed with `seq`; kept until sync returns it, and resent if a
   *       database failover lost it.
   * failed: refused for good (e.g. access ended); shown with `error`.
   */
  state: "pending" | "sent" | "failed"
  seq?: number
  error?: string
}

type StoreName = "messages" | "outbox" | "meta"

export class LocalDb {
  private constructor(private db: IDBDatabase) {}

  static open(personId: string): Promise<LocalDb> {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(`johku-hub-${personId}`, 1)
      req.onupgradeneeded = () => {
        const db = req.result
        db.createObjectStore("messages", { keyPath: "id" }).createIndex("threadId", "threadId")
        db.createObjectStore("outbox", { keyPath: "id" })
        db.createObjectStore("meta")
      }
      req.onsuccess = () => resolve(new LocalDb(req.result))
      req.onerror = () => reject(req.error)
    })
  }

  static delete(personId: string): Promise<void> {
    return new Promise((resolve) => {
      const req = indexedDB.deleteDatabase(`johku-hub-${personId}`)
      req.onsuccess = req.onerror = req.onblocked = () => resolve()
    })
  }

  close() {
    this.db.close()
  }

  private run<T>(stores: StoreName[], mode: IDBTransactionMode, fn: (tx: IDBTransaction) => IDBRequest<T> | void): Promise<T> {
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(stores, mode)
      const req = fn(tx)
      tx.oncomplete = () => resolve(req ? req.result : (undefined as T))
      tx.onerror = () => reject(tx.error)
      tx.onabort = () => reject(tx.error)
    })
  }

  allMessages(): Promise<LocalMessage[]> {
    return this.run(["messages"], "readonly", (tx) => tx.objectStore("messages").getAll())
  }

  allOutbox(): Promise<OutboxEntry[]> {
    return this.run(["outbox"], "readonly", (tx) => tx.objectStore("outbox").getAll())
  }

  putOutbox(entry: OutboxEntry): Promise<void> {
    return this.run(["outbox"], "readwrite", (tx) => {
      tx.objectStore("outbox").put(entry)
    })
  }

  deleteOutbox(id: string): Promise<void> {
    return this.run(["outbox"], "readwrite", (tx) => {
      tx.objectStore("outbox").delete(id)
    })
  }

  getMeta<T>(key: string): Promise<T | undefined> {
    return this.run(["meta"], "readonly", (tx) => tx.objectStore("meta").get(key))
  }

  setMeta(key: string, value: unknown): Promise<void> {
    return this.run(["meta"], "readwrite", (tx) => {
      tx.objectStore("meta").put(value, key)
    })
  }

  /**
   * Stores a page of synced messages and the new cursor in one transaction,
   * and drops the outbox entries those messages confirm. A crash can't leave
   * the cursor ahead of the stored messages.
   */
  applySyncPage(messages: LocalMessage[], cursor: number): Promise<void> {
    return this.run(["messages", "outbox", "meta"], "readwrite", (tx) => {
      const store = tx.objectStore("messages")
      const outbox = tx.objectStore("outbox")
      for (const m of messages) {
        store.put(m)
        outbox.delete(m.id)
      }
      tx.objectStore("meta").put(cursor, "cursor")
    })
  }

  /** Messages of threads that became visible below the cursor: the cursor stays where it is. */
  putMessages(messages: LocalMessage[]): Promise<void> {
    return this.run(["messages"], "readwrite", (tx) => {
      const store = tx.objectStore("messages")
      for (const m of messages) store.put(m)
    })
  }

  /** Drops the messages of threads the person no longer sees. */
  deleteThreads(threadIds: string[]): Promise<void> {
    return this.run(["messages"], "readwrite", (tx) => {
      const store = tx.objectStore("messages")
      const byThread = store.index("threadId")
      for (const id of threadIds) {
        const req = byThread.openKeyCursor(IDBKeyRange.only(id))
        req.onsuccess = () => {
          const c = req.result
          if (!c) return
          store.delete(c.primaryKey)
          c.continue()
        }
      }
    })
  }

  /** For a resync from the start after access changed. The outbox stays. */
  clearMessages(): Promise<void> {
    return this.run(["messages", "meta"], "readwrite", (tx) => {
      tx.objectStore("messages").clear()
      tx.objectStore("meta").put(0, "cursor")
    })
  }
}
